// Minimal encoder for palette (indexed) PNGs: the browser's own encoders only write truecolor PNGs.
// Used for the blank image of a new canvas (1 color) and for the lighter PNG exports (up to 256 colors).
// Needs CompressionStream (every current browser); callers keep a fallback for older ones.

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

let crcTable: Uint32Array | null = null;

const crc32 = (bytes: Uint8Array): number => {
  if (!crcTable) {
    crcTable = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
      let c = n;
      for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      crcTable[n] = c >>> 0;
    }
  }
  let crc = 0xffffffff;
  for (const byte of bytes) crc = crcTable[(crc ^ byte) & 0xff] ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
};

const chunk = (type: string, data: Uint8Array): Uint8Array<ArrayBuffer> => {
  const out = new Uint8Array(12 + data.length);
  const view = new DataView(out.buffer);
  view.setUint32(0, data.length);
  for (let i = 0; i < 4; i++) out[4 + i] = type.charCodeAt(i);
  out.set(data, 8);
  view.setUint32(8 + data.length, crc32(out.subarray(4, 8 + data.length)));
  return out;
};

// zlib-wrapped deflate, which is what PNG's IDAT expects.
const deflate = async (data: Uint8Array<ArrayBuffer>): Promise<Uint8Array<ArrayBuffer>> => {
  const stream = new ReadableStream<BufferSource>({
    start(controller) {
      controller.enqueue(data);
      controller.close();
    },
  }).pipeThrough(new CompressionStream('deflate'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
};

export const canEncodeIndexedPng = () => typeof CompressionStream === 'function';

interface IndexedPngOptions {
  width: number;
  height: number;
  // Up to 256 [r, g, b] colors.
  palette: number[][];
  // One palette index per pixel, row by row. Omitted: every pixel is the first color (stored at 1 bit per
  // pixel, so a blank 5000x5000 image is ~3 MB of zeros before deflating to a few KB).
  indices?: Uint8Array;
}

export const encodeIndexedPng = async ({ width, height, palette, indices }: IndexedPngOptions): Promise<Blob> => {
  const bitDepth = indices ? 8 : 1;
  const header = new Uint8Array(13);
  const headerView = new DataView(header.buffer);
  headerView.setUint32(0, width);
  headerView.setUint32(4, height);
  header[8] = bitDepth;
  header[9] = 3; // color type: palette

  // Each row: filter byte (0 = none) + the row's indices.
  const rowLength = indices ? width : Math.ceil(width / 8);
  const raw = new Uint8Array(height * (1 + rowLength));
  if (indices) {
    for (let y = 0; y < height; y++) raw.set(indices.subarray(y * width, (y + 1) * width), y * (1 + width) + 1);
  }

  const parts: BlobPart[] = [
    new Uint8Array(PNG_SIGNATURE),
    chunk('IHDR', header),
    chunk('PLTE', new Uint8Array(palette.flatMap(([r, g, b]) => [r, g, b]))),
    chunk('IDAT', await deflate(raw)),
    chunk('IEND', new Uint8Array(0)),
  ];
  return new Blob(parts, { type: 'image/png' });
};
